import type { CompareOp, Node, ParseError, ParseResult, Token, Value } from './ast';

// A query is parsed as it is typed, so the parser never throws: an unclosed quote or bracket is closed at the end, a
// stray operator is skipped, and each repair is reported as an error the query bar can underline.

type Item =
    | { t: 'lparen'; start: number }
    | { t: 'rparen'; start: number }
    | { t: 'not'; start: number }
    | { t: 'and'; start: number }
    | { t: 'or'; start: number }
    | { t: 'term'; node: Node; start: number; end: number };

const FIELD = /^([A-Za-z_@$][\w.@$-]*):/;
const SPACE = /\s/;
const KEYWORDS = new Set(['AND', 'OR', 'NOT']);

interface Lexed {
    items: Item[];
    tokens: Token[];
    errors: ParseError[];
}

// A quoted run: a phrase between double quotes or a regex between slashes, with backslash escapes.
function readDelimited(q: string, start: number, delimiter: string): { text: string; end: number; closed: boolean } {
    let text = '';
    let i = start + 1;
    while (i < q.length) {
        const c = q[i] as string;
        if (c === '\\' && i + 1 < q.length) {
            const next = q[i + 1] as string;
            text += next === delimiter ? next : c + next;
            i += 2;
            continue;
        }
        if (c === delimiter) {
            return { text, end: i + 1, closed: true };
        }
        text += c;
        i += 1;
    }

    return { text, end: i, closed: false };
}

// A regex only if its closing slash ends the term: `/timeout|ECONNRESET/` is one, `/auctions/listing` is a path.
function regexAt(q: string, start: number): { text: string; end: number } | null {
    const run = readDelimited(q, start, '/');
    if (!run.closed || run.text === '') {
        return null;
    }
    const after = q[run.end];

    return after === undefined || SPACE.test(after) || after === ')' ? { text: run.text, end: run.end } : null;
}

function readWord(q: string, start: number): number {
    let i = start;
    while (i < q.length && !SPACE.test(q[i] as string) && q[i] !== '(' && q[i] !== ')') {
        i += 1;
    }

    return i;
}

const wordValue = (text: string): Value => ({ kind: text.includes('*') ? 'wildcard' : 'word', text });

function lex(q: string): Lexed {
    const items: Item[] = [];
    const tokens: Token[] = [];
    const errors: ParseError[] = [];

    // One value after `field:` or inside `field:( … )`: a phrase, a regex or a word, ending at `stop` characters.
    const readValue = (start: number): { value: Value; end: number } => {
        if (q[start] === '"') {
            const run = readDelimited(q, start, '"');
            tokens.push({ type: 'value', kind: 'phrase', start, end: run.end });
            if (!run.closed) {
                errors.push({ message: 'Unclosed quote', start, end: run.end });
            }

            return { value: { kind: 'phrase', text: run.text }, end: run.end };
        }
        if (q[start] === '/') {
            const regex = regexAt(q, start);
            if (regex) {
                tokens.push({ type: 'value', kind: 'regex', start, end: regex.end });

                return { value: { kind: 'regex', text: regex.text }, end: regex.end };
            }
        }
        const end = readWord(q, start);
        const value = wordValue(q.slice(start, end));
        tokens.push({ type: 'value', kind: value.kind, start, end });

        return { value, end };
    };

    let i = 0;
    while (i < q.length) {
        const c = q[i] as string;
        if (SPACE.test(c)) {
            i += 1;
            continue;
        }
        if (c === '(' || c === ')') {
            items.push({ t: c === '(' ? 'lparen' : 'rparen', start: i });
            tokens.push({ type: 'paren', start: i, end: i + 1 });
            i += 1;
            continue;
        }
        if (c === '-' && i + 1 < q.length && !SPACE.test(q[i + 1] as string) && q[i + 1] !== ')') {
            items.push({ t: 'not', start: i });
            tokens.push({ type: 'negation', start: i, end: i + 1 });
            i += 1;
            continue;
        }

        const bareEnd = readWord(q, i);
        const bare = q.slice(i, bareEnd);
        if (KEYWORDS.has(bare)) {
            items.push({ t: bare === 'AND' ? 'and' : bare === 'OR' ? 'or' : 'not', start: i });
            tokens.push({ type: 'keyword', start: i, end: bareEnd });
            i = bareEnd;
            continue;
        }

        if (c === '"') {
            const run = readDelimited(q, i, '"');
            items.push({ t: 'term', node: { type: 'text', value: { kind: 'phrase', text: run.text } }, start: i, end: run.end });
            tokens.push({ type: 'text', kind: 'phrase', start: i, end: run.end });
            if (!run.closed) {
                errors.push({ message: 'Unclosed quote', start: i, end: run.end });
            }
            i = run.end;
            continue;
        }

        if (c === '/') {
            const regex = regexAt(q, i);
            if (regex) {
                items.push({ t: 'term', node: { type: 'text', value: { kind: 'regex', text: regex.text } }, start: i, end: regex.end });
                tokens.push({ type: 'text', kind: 'regex', start: i, end: regex.end });
                i = regex.end;
                continue;
            }
        }

        const field = FIELD.exec(q.slice(i));
        const afterColon = field ? i + field[0].length : -1;
        const next = field ? q[afterColon] : undefined;
        // `for:` at the end of a word and `https://…` are text, not fields.
        if (field && next !== undefined && !SPACE.test(next) && next !== ')' && !(next === '/' && q[afterColon + 1] === '/')) {
            const name = field[1] as string;
            tokens.push({ type: 'field', start: i, end: afterColon });
            let j = afterColon;
            let op: CompareOp = '=';
            const values: Value[] = [];

            if (q[j] === '(') {
                tokens.push({ type: 'paren', start: j, end: j + 1 });
                j += 1;
                let closed = false;
                while (j < q.length) {
                    const v = q[j] as string;
                    if (SPACE.test(v)) {
                        j += 1;
                        continue;
                    }
                    if (v === ')') {
                        tokens.push({ type: 'paren', start: j, end: j + 1 });
                        j += 1;
                        closed = true;
                        break;
                    }
                    const keywordEnd = readWord(q, j);
                    if (q.slice(j, keywordEnd) === 'OR') {
                        tokens.push({ type: 'keyword', start: j, end: keywordEnd });
                        j = keywordEnd;
                        continue;
                    }
                    const read = readValue(j);
                    values.push(read.value);
                    j = Math.max(read.end, j + 1);
                }
                if (!closed) {
                    errors.push({ message: 'Unclosed bracket', start: afterColon, end: j });
                }
            } else {
                const compare = /^(>=|<=|>|<)/.exec(q.slice(j));
                if (compare) {
                    op = compare[1] as CompareOp;
                    tokens.push({ type: 'operator', start: j, end: j + op.length });
                    j += op.length;
                }
                const read = readValue(j);
                if (read.value.text !== '' || read.value.kind === 'phrase') {
                    values.push(read.value);
                } else {
                    errors.push({ message: `A value is missing after ${name}:`, start: i, end: j });
                }
                j = read.end;
            }

            if (values.length > 0) {
                const node: Node = name === 'has' ? { type: 'has', field: values[0]?.text ?? '' } : { type: 'field', field: name, op, values };
                items.push({ t: 'term', node, start: i, end: j });
            }
            i = j;
            continue;
        }

        const value = wordValue(bare);
        items.push({ t: 'term', node: { type: 'text', value }, start: i, end: bareEnd });
        tokens.push({ type: 'text', kind: value.kind, start: i, end: bareEnd });
        i = bareEnd;
    }

    return { items, tokens, errors };
}

const flatten = (type: 'and' | 'or', children: Node[]): Node => {
    const flat = children.flatMap((child) => (child.type === type ? child.children : [child]));

    return flat.length === 1 ? (flat[0] as Node) : { type, children: flat };
};

export function parse(q: string): ParseResult {
    const { items, tokens, errors } = lex(q);
    let pos = 0;
    const peek = (): Item | undefined => items[pos];

    // NOT binds tightest, then AND (spaces are AND), then OR.
    const parseOr = (): Node | null => {
        const branches: Node[] = [];
        const first = parseAnd();
        if (first) {
            branches.push(first);
        }
        while (peek()?.t === 'or') {
            const or = items[pos] as Item;
            pos += 1;
            const next = parseAnd();
            if (next) {
                branches.push(next);
            } else {
                errors.push({ message: 'Nothing after OR', start: or.start, end: or.start + 2 });
            }
        }

        return branches.length === 0 ? null : flatten('or', branches);
    };

    const parseAnd = (): Node | null => {
        const parts: Node[] = [];
        for (let item = peek(); item && item.t !== 'or' && item.t !== 'rparen'; item = peek()) {
            if (item.t === 'and') {
                pos += 1;
                continue;
            }
            const node = parseUnary();
            if (node) {
                parts.push(node);
            }
        }

        return parts.length === 0 ? null : flatten('and', parts);
    };

    const parseUnary = (): Node | null => {
        const item = peek();
        if (item?.t === 'not') {
            pos += 1;
            const child = parseUnary();
            if (!child) {
                errors.push({ message: 'Nothing to exclude', start: item.start, end: item.start + 1 });

                return null;
            }

            return child.type === 'not' ? child.child : { type: 'not', child };
        }

        return parsePrimary();
    };

    const parsePrimary = (): Node | null => {
        const item = peek();
        if (!item) {
            return null;
        }
        pos += 1;
        if (item.t === 'term') {
            return item.node;
        }
        if (item.t === 'lparen') {
            const inner = parseOr();
            if (peek()?.t === 'rparen') {
                pos += 1;
            } else {
                errors.push({ message: 'Unclosed bracket', start: item.start, end: q.length });
            }

            return inner;
        }
        // A stray AND, NOT or OR has been dealt with by the callers; what is left here is a lone ')' or a dangling 'NOT'.
        errors.push({ message: `Unexpected ${item.t === 'rparen' ? ')' : item.t.toUpperCase()}`, start: item.start, end: item.start + 1 });

        return null;
    };

    let ast: Node | null = null;
    const parts: Node[] = [];
    while (pos < items.length) {
        const node = parseOr();
        if (node) {
            parts.push(node);
        }
        // A ')' with no '(' before it: skip it and go on.
        if (peek()?.t === 'rparen') {
            const stray = items[pos] as Item;
            errors.push({ message: 'Unexpected )', start: stray.start, end: stray.start + 1 });
            pos += 1;
        } else if (pos < items.length && !node) {
            pos += 1;
        }
    }
    if (parts.length > 0) {
        ast = flatten('and', parts);
    }

    return { ast, tokens, errors };
}
